import { formatINR } from '@/lib/formatters';
import { GST_RATE_NUMERIC, type GstRate } from '@/types/product';
import { cn } from '@/lib/cn';

interface PriceDisplayProps {
  /** Final price in paise. */
  paise: number;
  /** Optional MRP (paise) for strikethrough display. */
  originalPaise?: number | null;
  /** Optional GST rate enum (RATE_5 | RATE_12) — shows "(incl. X% GST)" */
  gstRate?: GstRate;
  /** Show the GST inclusion note (default true when gstRate provided). */
  showGstNote?: boolean;
  /** Render layout — inline (price next to MRP) or stacked (vertical). */
  layout?: 'inline' | 'stacked';
  /** Visual size — md is default; lg used on PDP. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClass: Record<NonNullable<PriceDisplayProps['size']>, string> = {
  sm: 'text-[14px]',
  md: 'text-[18px]',
  lg: 'text-[24px]',
  xl: 'text-[32px]',
};

export function PriceDisplay({
  paise,
  originalPaise,
  gstRate,
  showGstNote,
  layout = 'inline',
  size = 'md',
  className,
}: PriceDisplayProps) {
  const showStrike = originalPaise && originalPaise > paise;
  const discountPct = showStrike
    ? Math.round(((originalPaise - paise) / originalPaise) * 100)
    : 0;
  const ratePct = gstRate ? GST_RATE_NUMERIC[gstRate] * 100 : 0;
  const noteVisible = (showGstNote ?? !!gstRate) && !!gstRate;

  return (
    <div
      className={cn(
        'flex',
        layout === 'inline' ? 'flex-row flex-wrap items-baseline gap-3' : 'flex-col gap-1',
        className
      )}
    >
      <span
        className={cn(
          'font-[family-name:var(--font-display)] font-semibold tracking-tight text-white',
          sizeClass[size]
        )}
      >
        {formatINR(paise)}
      </span>
      {showStrike && (
        <span className={cn(
          'font-[family-name:var(--font-display)] font-normal tracking-tight text-[color:var(--color-text-muted)] line-through',
          sizeClass[size]
        )}>
          {formatINR(originalPaise!)}
        </span>
      )}
      {showStrike && (
        <span className="text-[12px] font-[family-name:var(--font-label)] uppercase tracking-[0.12em] text-[color:var(--color-success)]">
          {discountPct}% OFF
        </span>
      )}
      {noteVisible && (
        <span className="text-[11px] font-[family-name:var(--font-label)] uppercase tracking-[0.12em] text-[color:var(--color-text-muted)]">
          incl. {ratePct.toFixed(0)}% GST
        </span>
      )}
    </div>
  );
}
